import React from 'react';
import {
  AlertTriangle, Download, GitCompare, History, Plus, RefreshCw, Shield,
} from 'lucide-react';
import { openapiApi } from '@/config/api';
import { useNavigate } from 'react-router-dom';

function Pill({ value }) {
  const tone = {
    Draft: 'bg-slate-50 text-slate-700 border-slate-200',
    'In Review': 'bg-sky-50 text-sky-800 border-sky-200',
    Approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    Published: 'bg-teal-50 text-teal-900 border-teal-200',
    Rejected: 'bg-red-50 text-red-800 border-red-200',
    'Needs Review': 'bg-amber-50 text-amber-900 border-amber-200',
    Passed: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    Failed: 'bg-red-50 text-red-800 border-red-200',
    Warning: 'bg-amber-50 text-amber-800 border-amber-200',
    Breaking: 'bg-red-50 text-red-800 border-red-200',
    'Non-Breaking': 'bg-emerald-50 text-emerald-800 border-emerald-200',
    'Potentially Breaking': 'bg-amber-50 text-amber-800 border-amber-200',
    Added: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    Removed: 'bg-red-50 text-red-800 border-red-200',
    Modified: 'bg-amber-50 text-amber-800 border-amber-200',
  };
  return (
    <span className={`inline-flex rounded border px-2 py-0.5 text-[11px] font-bold ${tone[value] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
      {value || '—'}
    </span>
  );
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'registry', label: 'Version Registry' },
  { id: 'detail', label: 'Release Detail' },
  { id: 'compare', label: 'Compare Versions' },
  { id: 'changelog', label: 'Changelog' },
  { id: 'approval', label: 'Approval' },
  { id: 'deprecation', label: 'Deprecation' },
  { id: 'publication', label: 'Gateway Readiness' },
];

function ConsumerImpactPanel({ version }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!version) return undefined;
    openapiApi.get(`/openapi/releases/${encodeURIComponent(version)}/consumer-impact`)
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null));
    return undefined;
  }, [version]);
  if (!data) return null;
  return (
    <div>
      <div className="mb-1 text-xs font-bold uppercase text-slate-500">Consumer impact</div>
      <p className="mb-2 text-[11px] text-slate-400">{data.note}</p>
      <ul className="space-y-1 text-xs text-slate-600">
        {(data.consumers || []).map((c) => (
          <li key={c.name} className="flex flex-wrap gap-2 border-t border-slate-50 py-1">
            <span className="font-semibold">{c.name}</span>
            <span>· {c.access}</span>
            <span>· usage: {c.lastKnownUsage}</span>
            <span>· migration: {c.migrationStatus}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ApiReleaseCenter() {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState('overview');
  const [data, setData] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [selected, setSelected] = React.useState('');
  const [compare, setCompare] = React.useState(null);
  const [fromV, setFromV] = React.useState('baseline');
  const [toV, setToV] = React.useState('current');
  const [deprecations, setDeprecations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [draftForm, setDraftForm] = React.useState({ version: '', title: '', description: '' });
  const [reason, setReason] = React.useState('');
  const [approveGatewayCandidate, setApproveGatewayCandidate] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, depRes] = await Promise.all([
        openapiApi.get('/openapi/releases'),
        openapiApi.get('/openapi/releases/deprecations'),
      ]);
      setData(listRes.data?.data || null);
      setDeprecations(depRes.data?.data?.items || []);
      const first = listRes.data?.data?.releases?.[0]?.contractVersion;
      if (first && !selected) setSelected(first);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Gagal memuat release registry');
    } finally {
      setLoading(false);
    }
  }, [selected]);

  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!selected) return undefined;
    openapiApi.get(`/openapi/releases/${selected}`)
      .then((res) => setDetail(res.data?.data || null))
      .catch(() => setDetail(null));
    return undefined;
  }, [selected]);

  const runCompare = async () => {
    setBusy(true);
    try {
      const res = await openapiApi.get('/openapi/releases/compare', { params: { from: fromV, to: toV } });
      setCompare(res.data?.data || null);
      setTab('compare');
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const createDraft = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await openapiApi.post('/openapi/releases', draftForm);
      await load();
      setSelected(res.data?.data?.manifest?.contractVersion || draftForm.version);
      setTab('detail');
      setDraftForm({ version: '', title: '', description: '' });
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const transition = async (status) => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      await openapiApi.post(`/openapi/releases/${selected}/transition`, { status, reason, approveGatewayCandidate: status === 'Approved' && approveGatewayCandidate });
      setApproveGatewayCandidate(false);
      setReason('');
      await load();
      const res = await openapiApi.get(`/openapi/releases/${selected}`);
      setDetail(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const rollback = async () => {
    if (!selected || !window.confirm(`Rollback documentation pointer ke ${selected}? Backend/Gateway tidak di-rollback.`)) return;
    setBusy(true);
    try {
      await openapiApi.post(`/openapi/releases/${selected}/rollback`, { toVersion: selected, reason: reason || 'Documentation rollback' });
      setReason('');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const download = async (artifact) => {
    const res = await openapiApi.get(`/openapi/releases/${selected}/artifacts/${artifact}`, {
      responseType: artifact.endsWith('.md') ? 'text' : 'json',
    });
    const text = typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2);
    const blob = new Blob([text], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selected}-${artifact}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !data) {
    return <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Memuat Release Management…</div>;
  }

  const overview = data?.overview || {};
  const releases = data?.releases || [];
  const versions = ['baseline', 'current', ...releases.map((r) => r.contractVersion)];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">API Release Management</h2>
            <p className="mt-1 text-sm text-slate-600">
              Versioning dokumentasi/kontrak (SemVer), snapshot immutable, changelog, breaking-change governance, approval, dan Gateway readiness.
              Bukan runtime <code className="text-xs">/api/v2</code> otomatis.
            </p>
            <p className="mt-1 text-[11px] text-slate-400">{data?.policy?.note}</p>
          </div>
          <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
        {error && <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${tab === t.id ? 'border-teal-700 text-teal-900' : 'border-transparent text-slate-500'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            {[
              ['Approved', overview.currentApprovedVersion],
              ['Published docs', overview.latestPublishedDocumentation],
              ['Pending review', overview.pendingReview],
              ['Drafts', overview.drafts],
              ['Breaking pending', overview.breakingPending],
              ['Deprecated ops', overview.deprecatedOperations],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-2 text-xl font-bold text-slate-900">{value ?? '—'}</div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
            API publication readiness: <Pill value={overview.apiPublicationReadiness || 'Pending Verification'} />
            <p className="mt-2 text-xs text-slate-500">Documentation Published ≠ Backend Deployed ≠ Gateway Activated ≠ Developer Portal Published.</p>
          </div>
        </div>
      )}

      {tab === 'registry' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-bold"><Plus className="h-4 w-4" /> Create draft from current OpenAPI</div>
            <div className="grid gap-2 md:grid-cols-3">
              <input className="h-9 rounded-md border border-slate-200 px-3 text-sm" placeholder="SemVer e.g. 0.1.0" value={draftForm.version} onChange={(e) => setDraftForm((s) => ({ ...s, version: e.target.value }))} />
              <input className="h-9 rounded-md border border-slate-200 px-3 text-sm" placeholder="Release title" value={draftForm.title} onChange={(e) => setDraftForm((s) => ({ ...s, title: e.target.value }))} />
              <button type="button" disabled={busy || !draftForm.version} onClick={createDraft} className="h-9 rounded-md bg-slate-900 text-sm font-semibold text-white disabled:opacity-50">Create Draft</button>
            </div>
            <textarea className="w-full rounded-md border border-slate-200 p-2 text-sm" rows={2} placeholder="Description" value={draftForm.description} onChange={(e) => setDraftForm((s) => ({ ...s, description: e.target.value }))} />
            <p className="text-[11px] text-slate-400">Jangan mengarang 1.0.0 jika belum ada baseline yang disetujui — gunakan versi yang diverifikasi tim.</p>
          </div>
          <div className="overflow-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Version</th>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Breaking</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {releases.map((r) => (
                  <tr key={r.contractVersion} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono font-semibold">{r.contractVersion}</td>
                    <td className="px-3 py-2">{r.releaseTitle}</td>
                    <td className="px-3 py-2">{r.releaseType}</td>
                    <td className="px-3 py-2"><Pill value={r.status} /></td>
                    <td className="px-3 py-2"><Pill value={r.breakingChangeStatus} /></td>
                    <td className="px-3 py-2 text-xs">{r.createdAt ? new Date(r.createdAt).toLocaleString('id-ID') : '—'}</td>
                    <td className="px-3 py-2">
                      <button type="button" className="text-xs font-semibold text-teal-800" onClick={() => { setSelected(r.contractVersion); setTab('detail'); }}>Open</button>
                    </td>
                  </tr>
                ))}
                {!releases.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">Belum ada release — buat Draft terlebih dahulu.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(tab === 'detail' || tab === 'changelog' || tab === 'approval' || tab === 'publication') && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={selected} onChange={(e) => setSelected(e.target.value)}>
              <option value="">Select version</option>
              {releases.map((r) => <option key={r.contractVersion} value={r.contractVersion}>{r.contractVersion} ({r.status})</option>)}
            </select>
            {detail?.manifest && <Pill value={detail.manifest.status} />}
            {detail?.manifest && <Pill value={detail.manifest.breakingChangeStatus} />}
          </div>

          {tab === 'detail' && detail && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
              <h3 className="font-bold text-slate-900">{detail.manifest.releaseTitle}</h3>
              <p className="text-sm text-slate-600">{detail.manifest.description || '—'}</p>
              <div className="grid gap-2 text-xs md:grid-cols-2">
                <div>Contract: <code>{detail.manifest.contractVersion}</code></div>
                <div>OpenAPI: {detail.manifest.openapiSpecificationVersion}</div>
                <div>Runtime: {detail.manifest.runtimeApiVersion}</div>
                <div>SHA-256: <code className="break-all">{detail.manifest.artifactSha256}</code></div>
                <div>Commit: {detail.manifest.sourceCommitSha || '—'}</div>
                <div>Quality: <Pill value={detail.manifest.qualityGateResult} /></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-semibold" onClick={() => navigate(`/admin/internal-tools/api-documentation?tab=openapi&docsVersion=${encodeURIComponent(selected)}`)}>
                  View Documentation
                </button>
                <button type="button" className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-semibold" onClick={() => download('openapi.json')}><Download className="h-3 w-3" /> OpenAPI</button>
                <button type="button" className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-semibold" onClick={() => download('changelog.md')}>Changelog</button>
                <button type="button" className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-semibold" onClick={() => download('diff.json')}>Diff</button>
                <button type="button" className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-semibold" onClick={() => navigate('/admin/internal-tools/api-documentation?tab=quality')}>Quality Report</button>
              </div>
              <div>
                <div className="mb-1 text-xs font-bold uppercase text-slate-500">Diff summary</div>
                <pre className="overflow-auto rounded bg-slate-50 p-3 text-[11px]">{JSON.stringify(detail.diff?.summary || {}, null, 2)}</pre>
              </div>
              <div>
                <div className="mb-1 flex items-center gap-1 text-xs font-bold uppercase text-slate-500"><History className="h-3 w-3" /> Audit</div>
                <ul className="max-h-40 overflow-auto space-y-1 text-xs text-slate-600">
                  {(detail.audit || []).map((a, i) => (
                    <li key={i}>{a.at}: {a.type} {a.actor?.nip ? `by ${a.actor.nip}` : ''} {a.reason ? `— ${a.reason}` : ''}</li>
                  ))}
                </ul>
              </div>
              <ConsumerImpactPanel version={selected} />
            </div>
          )}

          {tab === 'changelog' && detail && (
            <pre className="overflow-auto rounded-lg border border-slate-200 bg-white p-4 text-xs whitespace-pre-wrap">{detail.changelog || 'No changelog'}</pre>
          )}

          {tab === 'approval' && detail && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-sm font-bold"><Shield className="h-4 w-4" /> Approval workspace</div>
              <p className="text-xs text-slate-500">Maker-checker: pembuat draft tidak boleh approve sendiri. Breaking changes butuh alasan eksplisit. Quality gate critical fail memblokir approve.</p>
              <textarea className="w-full rounded-md border border-slate-200 p-2 text-sm" rows={2} placeholder="Reason / notes" value={reason} onChange={(e) => setReason(e.target.value)} />
              <p className="text-xs text-slate-600">Generate candidate → Validate → Review → Approve → Freeze → Gateway Export. Candidate: {detail.manifest.gatewayCandidateValidation?.status || 'Not generated'} · {detail.manifest.gatewayCandidateOperations?.length || 0} operations.</p>
              {detail.manifest.status === 'In Review' && <label className="flex gap-2 text-xs"><input type="checkbox" checked={approveGatewayCandidate} onChange={e => setApproveGatewayCandidate(e.target.checked)} />Explicitly approve the validated Gateway POC operation set in this snapshot (reason required).</label>}
              <div className="flex flex-wrap gap-2">
                {detail.manifest.status === 'Draft' && <button type="button" disabled={busy} onClick={() => transition('In Review')} className="rounded-md bg-sky-700 px-3 py-2 text-sm font-semibold text-white">Submit Review</button>}
                {detail.manifest.status === 'In Review' && (
                  <>
                    <button type="button" disabled={busy} onClick={() => transition('Approved')} className="rounded-md bg-emerald-700 px-3 py-2 text-sm font-semibold text-white">Approve</button>
                    <button type="button" disabled={busy} onClick={() => transition('Rejected')} className="rounded-md border border-red-300 px-3 py-2 text-sm font-semibold text-red-700">Reject</button>
                    <button type="button" disabled={busy} onClick={() => transition('Draft')} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold">Back to Draft</button>
                  </>
                )}
                {detail.manifest.status === 'Approved' && (
                  <button type="button" disabled={busy} onClick={() => transition('Published')} className="rounded-md bg-teal-700 px-3 py-2 text-sm font-semibold text-white">Publish Documentation</button>
                )}
                {['Approved', 'Published'].includes(detail.manifest.status) && (
                  <button type="button" disabled={busy} onClick={rollback} className="rounded-md border border-amber-300 px-3 py-2 text-sm font-semibold text-amber-900">Rollback docs pointer</button>
                )}
              </div>
              {detail.manifest.breakingChangeStatus === 'Needs Review' && (
                <div className="flex gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> Breaking changes detected — MAJOR release recommended; explicit approval reason required.
                </div>
              )}
            </div>
          )}

          {tab === 'publication' && detail && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-sm font-bold">API Publication Readiness</div>
              <Pill value={detail.publicationManifest?.status || detail.manifest.apiManagement?.readiness} />
              <p className="text-xs text-slate-500">{detail.publicationManifest?.publicationPolicy}</p>
              <p className="text-xs text-slate-500">{detail.publicationManifest?.alignmentNote}</p>
              <pre className="overflow-auto rounded bg-slate-50 p-3 text-[11px]">{JSON.stringify(detail.publicationManifest || {}, null, 2)}</pre>
              <button type="button" className="rounded border border-slate-200 px-3 py-2 text-sm font-semibold" onClick={() => download('api-management-manifest.json')}>Download API Publication Manifest</button>
              <div className="flex items-start gap-2 text-xs text-amber-800"><AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> Tidak ada publish otomatis ke provider production. Status gateway/portal tetap Pending sampai konfirmasi ops.</div>
            </div>
          )}
        </div>
      )}

      {tab === 'compare' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-4">
            <label className="text-xs font-semibold">From
              <select className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" value={fromV} onChange={(e) => setFromV(e.target.value)}>
                {versions.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold">To
              <select className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" value={toV} onChange={(e) => setToV(e.target.value)}>
                {versions.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <button type="button" disabled={busy} onClick={runCompare} className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-3 text-sm font-semibold text-white">
              <GitCompare className="h-4 w-4" /> Compare
            </button>
          </div>
          {compare && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap gap-2 text-xs">
                <Pill value={`Added ${compare.diff.summary.added}`} />
                <Pill value={`Removed ${compare.diff.summary.removed}`} />
                <Pill value={`Modified ${compare.diff.summary.modified}`} />
                <Pill value={`Breaking ${compare.diff.summary.breaking}`} />
              </div>
              {[['Added', compare.diff.added, 'Added'], ['Removed', compare.diff.removed, 'Removed'], ['Breaking', compare.diff.breaking, 'Breaking']].map(([label, items, tone]) => (
                <div key={label}>
                  <div className="mb-1 text-xs font-bold uppercase text-slate-500">{label}</div>
                  <ul className="space-y-1 text-xs">
                    {(items || []).slice(0, 30).map((i, idx) => (
                      <li key={idx} className="flex gap-2"><Pill value={tone} /><span className="font-mono">{i.key || `${i.method} ${i.path}`}</span> {i.kind || ''}</li>
                    ))}
                    {!items?.length && <li className="text-slate-400">(none)</li>}
                  </ul>
                </div>
              ))}
              <div>
                <div className="mb-1 text-xs font-bold uppercase text-slate-500">Modified detail</div>
                <ul className="max-h-60 overflow-auto space-y-2 text-xs">
                  {(compare.diff.modified || []).slice(0, 40).map((m) => (
                    <li key={m.key} className="rounded border border-slate-100 p-2">
                      <div className="font-mono font-semibold">{m.key}</div>
                      <div className="text-slate-600">{m.changes.map((c) => `${c.severity}: ${c.kind}`).join(' · ')}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'deprecation' && (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-bold">Deprecation lifecycle</div>
          <p className="text-xs text-slate-500">Active → Deprecated → Sunset Scheduled → Retired. Header Deprecation/Sunset runtime memerlukan approval API change terpisah.</p>
          <table className="w-full text-left text-xs">
            <thead className="text-[10px] uppercase text-slate-500"><tr><th className="py-1">Op</th><th>Status</th><th>Sunset</th><th>Replacement</th></tr></thead>
            <tbody>
              {deprecations.map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="py-1.5 font-mono">{d.method} {d.path}</td>
                  <td><Pill value={d.status} /></td>
                  <td>{d.plannedSunsetDate || '—'}</td>
                  <td>{d.replacementEndpoint || '—'}</td>
                </tr>
              ))}
              {!deprecations.length && <tr><td colSpan={4} className="py-6 text-center text-slate-500">Belum ada deprecation tercatat</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
